import { useEffect, useMemo, useState } from 'react';
import type { EventListItemDto } from '@/services/platform/api/model';
import type { WhichSheetProps } from '../types';
import { cn } from '@/lib/core/utils/cn';
import { ImagesToImageMap } from '@/lib/core/utils';
import { useApp } from '@/lib/core/providers/appProvider';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import { Icon } from '@/components/ui/icon';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from '@/components/ui/radio-group';
import { BookHubs } from '@/components/shared/booking/common/book/hubs';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { HeartToggle } from '@/components/shared/shop/wishlist/ui/HeartToggle';
import { SoldOutTag } from '@/components/shared/shop';
import { useEvents, getEventDetail } from '@/services/platform/data/events';
import { isMembersOnly } from '@/components/shared/events/list/visibility';
import { cardSeatsLeft, isCardSoldOut } from '@/components/shared/events/rules';
import { formatCardDateTime } from '@/components/shared/events/list/format';
import { useEventCategoryLabel } from '@/components/shared/events/list/categories';
import { deriveBookingUpdatesFromEvent } from '../derive';

interface EventRowProps {
  event: EventListItemDto;
  disabled: boolean;
  locale?: string;
  isFavorited?: boolean;
  onToggleFavorite?: (event: EventListItemDto, next: boolean) => void;
  /** Whether this row is the current answer — see `onReselect`. */
  isSelected?: boolean;
  /**
   * Confirming the row that is already chosen. The radio group only fires `onValueChange` on an
   * actual change, so without this the sheet would sit open when the customer taps their existing
   * choice again.
   */
  onReselect?: () => void;
}

function EventRow({
  event,
  disabled,
  locale,
  isFavorited = false,
  onToggleFavorite,
  isSelected = false,
  onReselect,
}: EventRowProps) {
  const t = useClientTranslation();
  const getCategoryLabel = useEventCategoryLabel();
  const { appSettings } = useApp();
  const membersOnly = isMembersOnly(event.visibility);
  const isSoldOut = isCardSoldOut(event);
  const seatsLeft = cardSeatsLeft(event);
  const hasSeats = seatsLeft != null;
  const dateTime = formatCardDateTime(event.startDateTime, locale, event.endDateTime);
  const itemId = `which-${event.id}`;

  // Fall back to the CMS-configured no-pic image if the event has none or it fails to load;
  // the plain neutral placeholder (the container's own background) is the last resort.
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [event.imageUrl]);
  const showImage = !!event.imageUrl && !imageFailed;
  const fallbackImageUrl = ImagesToImageMap(appSettings?.properties?.eventNoPicImage)?.src;

  // `disabled` covers two distinct cases: sold out (permanent) and this row's own
  // selection being fetched (transient). Only the latter dims the whole row — a
  // sold-out row stays fully legible, with just the image itself faded.
  const isPending = disabled && !isSoldOut;

  return (
    <RadioGroupLabel
      htmlFor={itemId}
      className={cn(
        'flex w-full cursor-pointer items-stretch gap-3 py-4 text-[20px] tracking-normal text-[#1a1a1a]',
        disabled && 'pointer-events-none',
        isPending && 'opacity-40'
      )}
      // `preventDefault` stops the label from forwarding the click to its radio, which would bubble
      // back into this handler and run the selection twice.
      onClick={clickEvent => {
        if (disabled || !isSelected || !onReselect) return;
        clickEvent.preventDefault();
        onReselect();
      }}
    >
      <RadioGroupItem id={itemId} value={event.id ?? ''} disabled={disabled} className="mt-1" />

      <div className="bg-jncquoi-neutral-100 relative min-h-16 w-16 shrink-0 overflow-hidden rounded-[2px]">
        {showImage ? (
          <img
            src={event.imageUrl ?? undefined}
            alt=""
            className={cn('size-full object-cover', isSoldOut && 'opacity-40')}
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          fallbackImageUrl && (
            <img
              src={fallbackImageUrl}
              alt=""
              className={cn('size-full object-cover', isSoldOut && 'opacity-40')}
              loading="lazy"
            />
          )
        )}
        {isSoldOut && (
          <SoldOutTag className="absolute inset-x-0 bottom-2 w-full justify-center rounded-none px-1 whitespace-nowrap" />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          {membersOnly && (
            <>
              <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[10px] font-semibold tracking-[0.5px] uppercase">
                {t('App.Labels.MembersOnly')}
              </span>
              {event.eventCategory && (
                <span className="text-jncquoi-neutral-500 text-[4px]" aria-hidden="true">
                  ●
                </span>
              )}
            </>
          )}
          {event.eventCategory && (
            <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[10px] font-semibold tracking-[0.5px] uppercase">
              {getCategoryLabel(event.eventCategory)}
            </span>
          )}
        </div>
        <div className="flex items-start justify-between gap-2">
          <span
            className={cn(
              'font-pp-neue-montreal text-[16px] font-medium',
              isSoldOut ? 'text-jncquoi-neutral-700' : 'text-jncquoi-neutral-1000'
            )}
          >
            {event.title}
          </span>
          <HeartToggle
            size={24}
            isFavorited={isFavorited}
            disabled={disabled}
            onToggle={next => onToggleFavorite?.(event, next)}
          />
        </div>
        <div className="text-jncquoi-neutral-700 flex items-center gap-2 text-[12px]">
          {dateTime && <span className="whitespace-nowrap">{dateTime}</span>}
          {dateTime && hasSeats && (
            <span className="text-jncquoi-neutral-500 text-[4px]" aria-hidden="true">
              ●
            </span>
          )}
          {hasSeats && (
            <span className="font-pp-neue-montreal flex items-center gap-1 text-[10px] font-semibold tracking-wide text-[#325d2d] uppercase">
              <Icon name="user-badge-check" width={12} height={12} className="text-[#325d2d]" />
              {t('App.Events.SeatsLeft', { count: seatsLeft ?? 0 })}
            </span>
          )}
        </div>
        {event.venueName && (
          <div className="text-jncquoi-neutral-700 flex items-center gap-1 text-[12px]">
            <Icon name="map-pin" width={12} height={12} className="text-jncquoi-neutral-700 shrink-0" />
            <span className="truncate">{event.venueName}</span>
          </div>
        )}
      </div>
    </RadioGroupLabel>
  );
}

export function WhichSheet({ isOpen, onClose, cmsProps, booking, onBookingChange }: WhichSheetProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const ALL_HUB = t('App.Labels.All');
  const hubOptions = cmsProps?.hubs ?? [];
  const hubLabels = useMemo(
    () => [ALL_HUB, ...hubOptions.map(hub => hub.name).filter((name): name is string => !!name)],
    [hubOptions, ALL_HUB]
  );
  const [activeHubLabel, setActiveHubLabel] = useState(ALL_HUB);
  const hubKey =
    activeHubLabel === ALL_HUB ? undefined : hubOptions.find(hub => hub.name === activeHubLabel)?.id;

  // Favourite state is local to this sheet and does NOT persist — unlike the ELP, which
  // now writes through `EventFavoriteToggle`/`AddToWishlistSheet`. Picking an event is the
  // job here, so favouriting was left as a visual affordance only.
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const handleToggleFavorite = (event: EventListItemDto, next: boolean) => {
    const id = event.id;
    if (!id) return;
    setFavorites(prev => {
      const updated = new Set(prev);
      if (next) updated.add(id);
      else updated.delete(id);
      return updated;
    });
  };

  // `allowsBooking: true` keeps courtesy/no-booking events (Fluxo 1) out of this
  // booking flow — they're display-only on the Event Detail Page (add-to-calendar),
  // never a "Which" option here. `includeRestricted: false` — unlike the ELP, this is
  // a choice: a locked Members Only/Invite Only card the user can't actually pick
  // wouldn't make sense here, so non-members simply don't see it at all.
  // No `isCampaign`: campaigns are listed with the dated events, in the server's next-date order
  // (a campaign is dated by its next run day), not in a section of their own.
  const { events, isLoading: loadingEvents } = useEvents({
    hubKey,
    allowsBooking: true,
    includeRestricted: false,
    excludeExternal: true,
  });

  const selectableItems = useMemo(
    () => events.filter((event): event is EventListItemDto & { id: string } => !!event.id),
    [events]
  );

  async function handleValueChange(id: string) {
    const eventItem = selectableItems.find(event => event.id === id);
    if (!eventItem || pendingId) return;
    setPendingId(eventItem.id);
    try {
      const detail = await getEventDetail(eventItem.id, locale);
      if (!detail) return;
      onBookingChange(deriveBookingUpdatesFromEvent(detail));
      onClose();
    } finally {
      setPendingId(null);
    }
  }

  const isEmpty = !loadingEvents && events.length === 0;

  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="h-[80dvh] min-h-[min(650px,100dvh)]">
        <DrawerHeader className="shrink-0 gap-2 pt-8">
          <div className="flex items-center gap-8">
            <DrawerTitle className="flex-1 text-[32px] font-medium tracking-[0.5px]">
              {cmsProps?.title ?? t('App.Labels.Which')}
            </DrawerTitle>
            <DrawerClose
              className="flex size-11 cursor-pointer items-center justify-center rounded-full"
              aria-label="Close"
            >
              <Icon
                name="xmark"
                width={24}
                height={24}
                strokeWidth={1.5}
                className="text-jncquoi-neutral-1000"
              />
            </DrawerClose>
          </div>
          <DrawerDescription className="text-[16px] text-[#707070]">
            {cmsProps?.subtitle ?? t('App.Labels.EventWhichSubtitle')}
          </DrawerDescription>
        </DrawerHeader>

        {hubLabels.length > 1 && (
          <div className="flex shrink-0 px-4 py-4">
            <BookHubs hubs={hubLabels} activeHub={activeHubLabel} onHubChange={setActiveHubLabel} />
          </div>
        )}

        <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto px-4 pb-10">
          {loadingEvents && events.length === 0 ? (
            <SpinLoading wrapperClassName="py-10" />
          ) : isEmpty ? (
            <p className="text-jncquoi-neutral-600 py-10 text-center text-[14px]">
              {t('App.Events.EmptyTitle')}
            </p>
          ) : (
            <RadioGroup
              value={booking.event?.id ?? ''}
              onValueChange={handleValueChange}
              className="flex flex-col gap-0"
            >
              {events.map(event => (
                <EventRow
                  key={event.id}
                  event={event}
                  disabled={isCardSoldOut(event) || pendingId === event.id}
                  locale={locale}
                  isFavorited={!!event.id && favorites.has(event.id)}
                  onToggleFavorite={handleToggleFavorite}
                  isSelected={!!event.id && event.id === booking.event?.id}
                  onReselect={() => event.id && handleValueChange(event.id)}
                />
              ))}
            </RadioGroup>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
